import { Check, CircleNotch, Copy, DownloadSimple, Warning } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface Props {
  householdName: string;
  payload: unknown;
  isLoading: boolean;
  readinessPercent: number;
  readinessIssues: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
}

/**
 * Presentational — the parent triggers `useExportHousehold` and passes the
 * resulting payload/loading state down, keeping this dialog free of data
 * fetching (per the no-`useEffect`-for-loading rule).
 */
export function ExportJsonDialog({
  householdName,
  payload,
  isLoading,
  readinessPercent,
  readinessIssues,
  open,
  onOpenChange,
}: Props) {
  const [copied, setCopied] = useState(false);

  const json = payload ? JSON.stringify(payload, null, 2) : "";
  const byteSize = json ? new Blob([json]).size : 0;
  const slug = slugify(householdName) || "household";
  const filename = `${slug}.setupData.json`;
  const isReady = readinessPercent === 100;

  const handleCopy = () => {
    void navigator.clipboard?.writeText(json).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Handoff payload — {householdName}</DialogTitle>
          <DialogDescription>
            The <span className="font-mono">setupData</span> JSON handed off when onboarding
            completes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div
            className={cn(
              "flex items-start gap-2.5 rounded-xl p-3 text-[13.5px]",
              isReady ? "bg-success/10 text-success" : "bg-warning/10 text-warning",
            )}
          >
            <Warning className="mt-0.5 h-4 w-4 shrink-0" weight="light" />
            <div className="text-primary">
              <span className={cn("font-semibold", isReady ? "text-success" : "text-warning")}>
                {readinessPercent}% — {readinessIssues.length} items to confirm.
              </span>{" "}
              {readinessIssues.length > 0 &&
                "You can export now, but the payload has unresolved flags."}
              {readinessIssues.length > 0 && (
                <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[12.5px]">
                  {readinessIssues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Light Grey / Residential Grey on the dark #1c1815 panel — pass AA there, unlike on light surfaces */}
          <div className="max-h-[300px] overflow-auto rounded-xl bg-[#1c1815] p-4">
            {isLoading ? (
              <div className="flex items-center gap-2 py-8 text-[13px] text-[#c6bfb7]">
                <CircleNotch className="h-4 w-4 animate-spin" weight="light" />
                Loading…
              </div>
            ) : (
              <pre className="font-mono text-xs leading-relaxed whitespace-pre text-[#ebe6de]">
                {json}
              </pre>
            )}
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <span className="text-muted font-mono text-xs">
            {isLoading ? "—" : `${filename} · ${formatBytes(byteSize)}`}
          </span>
          <div className="flex gap-2.5">
            <Button variant="outline" type="button" onClick={handleCopy} disabled={!json}>
              {copied ? (
                <Check className="h-4 w-4" weight="light" />
              ) : (
                <Copy className="h-4 w-4" weight="light" />
              )}
              {copied ? "Copied" : "Copy JSON"}
            </Button>
            <Button type="button" onClick={handleDownload} disabled={!json}>
              <DownloadSimple className="h-4 w-4" weight="light" />
              Download .json
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
